import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { X } from 'lucide-react-native';
import type { ReactNode } from 'react';

import { KeyboardAwareActionsheet } from '@/components/KeyboardAwareActionsheet';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
} from '@/components/ui/modal';
import { VStack } from '@/components/ui/vstack';

interface MobileNumberSetupSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
}

/**
 * Platform-aware overlay container for the SMS phone-setup prompt. Renders a
 * centered `Modal` on web and a `KeyboardAwareActionsheet` on native — the
 * idiomatic presentation per platform — while the body (`children`, always a
 * `MobileNumberSetupForm`) stays identical.
 *
 * Single overlay shared by every standalone SMS prompt — expert first-message
 * and submitter post-inquiry — so the prompt looks and behaves the same across
 * them. The reference wizard renders the form inline in its own surface
 * instead (it can't stack a second overlay), so it does not use this.
 */
export function MobileNumberSetupSheet({ isOpen, onClose, children }: MobileNumberSetupSheetProps) {
  if (isWeb) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalBackdrop />

        <ModalContent>
          <ModalHeader className="justify-end">
            <ModalCloseButton onPress={onClose}>
              <Icon as={X} size="md" className="text-typography-500" />
            </ModalCloseButton>
          </ModalHeader>

          <ModalBody className="pb-4">{children}</ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <KeyboardAwareActionsheet isOpen={isOpen} onClose={onClose} closeLabel="Close">
      <VStack>{children}</VStack>
    </KeyboardAwareActionsheet>
  );
}
